(function () {
  'use strict';

  const ANIMATION_DURATION = 500;

  /**
   * Simple debounce helper.
   */
  function debounce(fn, wait) {
    let timeout;
    const debounced = function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
    debounced.cancel = () => clearTimeout(timeout);
    return debounced;
  }

  /**
   * Smoothly animate a numeric value from -> to over a duration.
   */
  function animateValue({
    from,
    to,
    duration,
    onUpdate,
    easing = (t) => t * t * (3 - 2 * t),
    onComplete,
  }) {
    const startTime = performance.now();
    let cancelled = false;
    let currentValue = from;

    function step(currentTime) {
      if (cancelled) return;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easing(progress);
      currentValue = from + (to - from) * eased;
      onUpdate(currentValue);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else if (typeof onComplete === 'function') {
        onComplete();
      }
    }

    requestAnimationFrame(step);

    return {
      get current() {
        return currentValue;
      },
      cancel() {
        cancelled = true;
      },
    };
  }

  class MarqueeComponent extends HTMLElement {
    constructor() {
      super();
      this._animation = null;
      this._marqueeWidth = null;
      this._wrapper = null;
      this._content = null;

      this._slowDown = debounce(this._slowDownImpl.bind(this), ANIMATION_DURATION);
      this._speedUp = this._speedUp.bind(this);
      this._handleResize = debounce(this._handleResizeImpl.bind(this), 250);
    }

    async connectedCallback() {
      this._wrapper = this.querySelector('[data-marquee-wrapper]');
      this._content = this.querySelector('[data-marquee-content]');
      const firstItems = this.querySelector('[data-marquee-items]');

      if (!this._wrapper || !this._content || !firstItems) return;

      const { numberOfCopies } = await this._queryNumberOfCopies();
      const speed = this._calculateSpeed(numberOfCopies);

      this._addRepeatedItems(numberOfCopies);
      this._duplicateContent();
      this._setSpeed(speed);

      window.addEventListener('resize', this._handleResize);
      this.addEventListener('pointerenter', this._slowDown);
      this.addEventListener('pointerleave', this._speedUp);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._handleResize);
      this.removeEventListener('pointerenter', this._slowDown);
      this.removeEventListener('pointerleave', this._speedUp);
    }

    _getItems() {
      if (!this._content) return [];
      return Array.from(this._content.querySelectorAll('[data-marquee-items]'));
    }

    _getClonedContent() {
      const last = this._wrapper.lastElementChild;
      return this._content !== last ? last : null;
    }

    _setSpeed(value) {
      this.style.setProperty('--marquee-speed', `${value}s`);
    }

    _slowDownImpl() {
      if (this._animation) return;
      const animation = this._wrapper.getAnimations()[0];
      if (!animation) return;

      this._animation = animateValue({
        duration: ANIMATION_DURATION,
        from: 1,
        to: 0,
        onUpdate: (v) => animation.updatePlaybackRate(v),
        onComplete: () => {
          this._animation = null;
        },
      });
    }

    _speedUp() {
      this._slowDown.cancel();
      const animation = this._wrapper.getAnimations()[0];
      if (!animation || animation.playbackRate === 1) return;

      const from = this._animation?.current ?? 0;
      this._animation?.cancel();

      this._animation = animateValue({
        duration: ANIMATION_DURATION,
        from,
        to: 1,
        onUpdate: (v) => animation.updatePlaybackRate(v),
        onComplete: () => {
          this._animation = null;
        },
      });
    }

    _queryNumberOfCopies() {
      const items = this._getItems();

      return new Promise((resolve) => {
        if (!items[0]) {
          return setTimeout(
            () => resolve({ numberOfCopies: 1, isHorizontalResize: true }),
            0
          );
        }

        const io = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            if (!entry) return;
            io.disconnect();

            const { width: marqueeWidth } = entry.rootBounds ?? { width: 0 };
            const { width: itemsWidth } = entry.boundingClientRect;

            const isHorizontalResize = this._marqueeWidth !== marqueeWidth;
            this._marqueeWidth = marqueeWidth;

            setTimeout(() => {
              resolve({
                numberOfCopies:
                  itemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / itemsWidth),
                isHorizontalResize,
              });
            }, 0);
          },
          { root: this }
        );

        io.observe(items[0]);
      });
    }

    _calculateSpeed(numberOfCopies) {
      const factor = Number(this.getAttribute('data-speed-factor')) || 25;
      return Math.sqrt(numberOfCopies) * factor;
    }

    async _handleResizeImpl() {
      const { numberOfCopies: newNum, isHorizontalResize } =
        await this._queryNumberOfCopies();

      if (!isHorizontalResize) return;

      const currentItems = this._getItems();
      const currentNum = currentItems.length;
      const speed = this._calculateSpeed(newNum);

      if (newNum > currentNum) {
        this._addRepeatedItems(newNum - currentNum);
      } else if (newNum < currentNum) {
        this._removeRepeatedItems(currentNum - newNum);
      }

      this._duplicateContent();
      this._setSpeed(speed);
      this._restartAnimation();
    }

    _restartAnimation() {
      const animations = this._wrapper.getAnimations();
      requestAnimationFrame(() => {
        for (const a of animations) a.currentTime = 0;
      });
    }

    _duplicateContent() {
      this._getClonedContent()?.remove();
      const clone = this._content.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true; // Claude fixes 62: hidden copy must not be reachable by keyboard
      this._wrapper.appendChild(clone);
    }

    _addRepeatedItems(numberOfCopies) {
      const items = this._getItems();
      if (!items[0]) return;

      for (let i = 0; i < numberOfCopies - 1; i++) {
        const clone = items[0].cloneNode(true);
        this._content.appendChild(clone);
      }
    }

    _removeRepeatedItems(numberOfCopies) {
      const children = Array.from(this._content.children);
      const toRemove = Math.min(numberOfCopies, children.length - 1);

      for (let i = 0; i < toRemove; i++) {
        this._content.lastElementChild?.remove();
      }
    }
  }

  if (!customElements.get('marquee-component')) {
    customElements.define('marquee-component', MarqueeComponent);
  }
})();